// Locale-aware date string formatting / parsing, extracted from the calendar
// widget so the date picker, date-time picker and the `x-h-date-format`
// directive can all share one implementation.

import { resolveLocale } from './language';

export const dateOrderMap = { Y: 'year', M: 'month', D: 'day' };

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
// Invisible directional marks (e.g. the U+200F RTL mark some locales add) and
// odd spaces (NBSP / narrow NBSP) must be stripped so regexes match user input.
const BIDI_MARKS = /[\u200E\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;
const ODD_SPACES = /[\u00A0\u202F]/g;
const REGEX_SPECIALS = /[.*+?^${}()|[\]\\]/g;
// Han, Hangul and Kana field names are whole words (年, 월), shown once.
const WHOLE_WORD_NAME = /^[\p{Script=Han}\p{Script=Hangul}\p{Script=Hiragana}\p{Script=Katakana}]+$/u;
const LATIN_TOKENS = { year: 'Y', month: 'M', day: 'D' };

// The letters a placeholder shows for each field, as native date inputs do - the
// initial of the locale's own field name (TT.MM.JJJJ, jj/mm/aaaa), a CJK name
// once (年/月/日), or Latin letters where those would be ambiguous.
function placeholderTokens(locale, parts) {
  const lengths = { year: parts.find((p) => p.type === 'year').value.length === 2 ? 2 : 4, month: 2, day: 2 };
  let names;
  try {
    const displayNames = new Intl.DisplayNames(locale, { type: 'dateTimeField' });
    names = { year: displayNames.of('year'), month: displayNames.of('month'), day: displayNames.of('day') };
  } catch {
    names = undefined;
  }
  const initial = (name) => [...name][0];
  const text = parts.map((p) => p.value).join('');
  const localized =
    names &&
    Object.values(names).every(Boolean) &&
    new Set(Object.values(names).map(initial)).size === 3 &&
    // A CJK name the format already writes (2001年3月5日) would read twice.
    !Object.values(names).some((name) => WHOLE_WORD_NAME.test(name) && text.includes(name));
  const tokens = {};
  for (const field of ['year', 'month', 'day']) {
    if (!localized) tokens[field] = LATIN_TOKENS[field].repeat(lengths[field]);
    else if (WHOLE_WORD_NAME.test(names[field])) tokens[field] = names[field];
    else tokens[field] = initial(names[field]).repeat(lengths[field]);
  }
  return tokens;
}

function isoDateToParts(value) {
  const iso = ISO_DATE.exec(value);
  return iso ? new Date(parseInt(iso[1]), parseInt(iso[2]) - 1, parseInt(iso[3])) : null;
}

// A parsed `Date` is usable only when it is a real, non-`NaN` date. Shared by
// the `x-h-date-format` directive and the `$dateFormat` magic.
function valid(d) {
  return d && !isNaN(d) ? d : undefined;
}

/**
 * Coerce an arbitrary model value into a `Date`. Accepts a `Date`, a timestamp,
 * an ISO `YYYY-MM-DD` string (parsed without timezone drift) or anything the
 * `Date` constructor understands. Returns `undefined` for empty input.
 */
export function toDate(value) {
  if (value === null || value === undefined || value === '') return undefined;
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  return isoDateToParts(String(value)) ?? new Date(value);
}

/**
 * Build a reusable date formatter/parser bound to a locale and display options.
 *
 * @param {{
 *   locale?: string,
 *   options?: Intl.DateTimeFormatOptions,
 *   delimiter?: string,        // override the locale's field separator
 *   order?: string,            // field order as a 'Y'/'M'/'D' string, e.g. 'DMY'
 *   rangeSeparator?: string,   // string between start/end in range output (default ' - ')
 * }} [config]
 * @returns {{
 *   format: (date: Date) => string,
 *   parse: (value: string) => Date,
 *   formatRange: (start?: Date, end?: Date) => string|undefined,
 *   parseRange: (value: string) => { start: Date, end?: Date },
 *   rangeSeparator: string,
 *   locale: string|undefined,
 *   formatter: Intl.DateTimeFormat,
 * }}
 */
export function createDateFormatter(config = {}) {
  const locale = config.locale || undefined;
  const delimiter = config.delimiter;
  const order = config.order;
  const rangeSeparator = config.rangeSeparator !== undefined ? config.rangeSeparator : ' - ';
  const formatter = config.options ? new Intl.DateTimeFormat(locale, config.options) : new Intl.DateTimeFormat(locale);

  let inputParser = null;
  let digitNormalizer = null;
  // The display format as a typing hint (mm/dd/yyyy), set only for a format with a pattern parser.
  let placeholder = undefined;

  function fieldOrderFromParts(parts) {
    return order ? [...order].map((c) => dateOrderMap[c]) : parts.filter((p) => p.type === 'year' || p.type === 'month' || p.type === 'day').map((p) => p.type);
  }

  function buildInputParser() {
    const probe = new Date(2001, 2, 5);
    const parts = formatter.formatToParts(probe);

    // Non-Gregorian calendars (e.g. Solar Hijri for fa-IR) require calendar conversion -
    // skip the parser and fall back to new Date(), which browsers handle better.
    const { calendar: resolvedCalendar, locale: resolvedLocale } = formatter.resolvedOptions();
    if (resolvedCalendar !== 'gregory' && resolvedCalendar !== 'iso8601') {
      inputParser = null;
      digitNormalizer = null;
      return;
    }

    // Build a digit normalizer for locales that use non-ASCII numerals (e.g. Arabic-Indic for ar-SA).
    const nf = new Intl.NumberFormat(resolvedLocale);
    if (nf.format(1) !== '1') {
      const digitMap = {};
      for (let i = 0; i <= 9; i++) digitMap[nf.format(i)] = String(i);
      digitNormalizer = (str) => str.replace(/./gu, (c) => digitMap[c] ?? c);
    }

    const monthPart = parts.find((p) => p.type === 'month');
    const normalizedMonth = monthPart ? (digitNormalizer ? digitNormalizer(monthPart.value) : monthPart.value) : null;
    if (normalizedMonth && !/^\d/.test(normalizedMonth)) {
      inputParser = null;
      return;
    }

    // Only a plain year/month/day format has a hint. A field keeps any text around its digits, such as the 월 of a Korean 3월.
    const hinted = parts.every((p) => p.type === 'year' || p.type === 'month' || p.type === 'day' || p.type === 'literal');
    const tokens = hinted && parts.some((p) => p.type === 'year') ? placeholderTokens(resolvedLocale, parts) : null;
    const hint = (part) => (digitNormalizer ? digitNormalizer(part.value) : part.value).replace(/\d+/, tokens[part.type]);

    if (order === undefined && delimiter === undefined) {
      // Default: iterate all parts so locale prefix/suffix literals are included in the regex.
      let regexStr = '^';
      const fieldOrder = [];
      for (const part of parts) {
        if (part.type === 'year') {
          regexStr += '(\\d{2,4})';
          fieldOrder.push('year');
        } else if (part.type === 'month') {
          regexStr += '(\\d{1,2})';
          fieldOrder.push('month');
        } else if (part.type === 'day') {
          regexStr += '(\\d{1,2})';
          fieldOrder.push('day');
        } else if (part.type === 'literal') {
          const normalized = part.value.replace(BIDI_MARKS, '').replace(ODD_SPACES, ' ');
          if (normalized) regexStr += normalized.replace(REGEX_SPECIALS, '\\$&');
        }
      }
      inputParser = fieldOrder.length === 3 ? { regex: new RegExp(regexStr + '$'), fieldOrder } : null;
      if (inputParser && tokens) placeholder = parts.map((p) => (p.type === 'literal' ? p.value : hint(p))).join('');
      return;
    }

    // Custom order or delimiter: format() strips locale prefix/suffix, so regex needs only fields + sep.
    const sep = delimiter !== undefined ? delimiter : (parts.find((p) => p.type === 'literal')?.value ?? '');
    const fieldOrder = fieldOrderFromParts(parts);
    if (fieldOrder.length !== 3 || fieldOrder.some((f) => f === undefined)) {
      inputParser = null;
      return;
    }

    const escapedSep = sep.replace(REGEX_SPECIALS, '\\$&');
    const regexStr = '^' + fieldOrder.map((f) => (f === 'year' ? '(\\d{2,4})' : '(\\d{1,2})')).join(escapedSep) + '$';
    inputParser = { regex: new RegExp(regexStr), fieldOrder };
    if (tokens) placeholder = fieldOrder.map((f) => hint(parts.find((p) => p.type === f))).join(sep);
  }

  function format(d) {
    if (delimiter === undefined && order === undefined) return formatter.format(d);

    const parts = formatter.formatToParts(d);
    const sep = delimiter !== undefined ? delimiter : (parts.find((p) => p.type === 'literal')?.value ?? '');
    const fieldValues = {};
    for (const p of parts) {
      if (p.type === 'year' || p.type === 'month' || p.type === 'day') fieldValues[p.type] = p.value;
    }
    return fieldOrderFromParts(parts)
      .map((f) => fieldValues[f])
      .join(sep);
  }

  function parse(value) {
    const iso = isoDateToParts(value);
    if (iso) return iso;
    if (inputParser) {
      let normalized = digitNormalizer ? digitNormalizer(value) : value;
      normalized = normalized.replace(BIDI_MARKS, '').replace(ODD_SPACES, ' ');
      const match = inputParser.regex.exec(normalized);
      if (match) {
        const fields = {};
        inputParser.fieldOrder.forEach((field, i) => {
          fields[field] = parseInt(match[i + 1]);
        });
        const year = fields.year < 100 ? 2000 + fields.year : fields.year;
        return new Date(year, fields.month - 1, fields.day);
      }
    }
    return new Date(value);
  }

  function formatRange(start, end) {
    if (!start) return undefined;
    return end ? `${format(start)}${rangeSeparator}${format(end)}` : format(start);
  }

  function parseRange(value) {
    const parts = String(value).split(rangeSeparator);
    const start = parse((parts[0] ?? '').trim());
    const end = parts.length > 1 ? parse((parts[1] ?? '').trim()) : undefined;
    return { start, end };
  }

  buildInputParser();

  return { format, parse, formatRange, parseRange, rangeSeparator, locale, formatter, placeholder };
}

/**
 * Registers the date-format plugin surface:
 *
 * - `x-h-date-format` directive - render a date value (or the element's own
 *   text content) as a locale-aware date string into the element's text
 *   content. Value source: the directive expression (a `Date`, timestamp, ISO
 *   string, or `{ start, end }` when `data-range="true"`); when there is no
 *   expression the element's current text content is used as the source value.
 *   Formatting is configured with `data-locale`, `data-order` (e.g. `DMY`),
 *   `data-delimiter`, `data-range`, `data-range-separator` and `data-options`
 *   (a JSON `Intl.DateTimeFormat` options object). Changing any of these
 *   re-renders. When `data-locale` is absent the locale falls back to the
 *   document's `<html lang>` then the browser locale (see `resolveLocale`).
 * - `$dateFormat` magic - the same formatting engine for use in Alpine
 *   expressions. `$dateFormat(value, config?)` returns a formatted string (or
 *   a formatted range when `value` is a `{ start, end }` object), and
 *   `$dateFormat.with(config?)` returns the full formatter object (with
 *   `format`/`parse`/`formatRange`/`parseRange`) for cases that also need to
 *   parse input back into a `Date`. Both apply the same locale fallback when
 *   `config.locale` is not set.
 */
export default function (Alpine) {
  Alpine.directive('h-date-format', (el, { expression }, { effect, evaluateLater, cleanup }) => {
    const sourceText = expression ? null : el.textContent.trim();
    const getValue = expression ? evaluateLater(expression) : null;
    let formatter = build();

    function build() {
      const config = {};
      config.locale = resolveLocale(el.getAttribute('data-locale'));
      const order = el.getAttribute('data-order');
      if (order) config.order = order;
      if (el.hasAttribute('data-delimiter')) config.delimiter = el.getAttribute('data-delimiter');
      if (el.hasAttribute('data-range-separator')) config.rangeSeparator = el.getAttribute('data-range-separator');
      const options = el.getAttribute('data-options');
      if (options) {
        try {
          config.options = JSON.parse(options);
        } catch {
          console.error('x-h-date-format: data-options must be a valid JSON object');
        }
      }
      return createDateFormatter(config);
    }

    function render(value) {
      let out;
      if (el.getAttribute('data-range') === 'true') {
        const { start, end } = value || {};
        const validStart = valid(toDate(start));
        out = validStart ? (formatter.formatRange(validStart, valid(toDate(end))) ?? '') : '';
        el.removeAttribute('datetime');
      } else {
        const d = valid(toDate(value));
        out = d ? formatter.format(d) : '';
        // Keep a machine-readable value on <time> elements.
        if (el.tagName === 'TIME') {
          if (d) el.setAttribute('datetime', `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
          else el.removeAttribute('datetime');
        }
      }
      el.textContent = out;
    }

    function refresh() {
      if (getValue) getValue((value) => render(value));
      else render(sourceText);
    }

    if (getValue) effect(() => getValue((value) => render(value)));
    else refresh();

    // Re-render when any formatting attribute changes.
    const observer = new MutationObserver(() => {
      formatter = build();
      refresh();
    });
    observer.observe(el, {
      attributes: true,
      attributeFilter: ['data-locale', 'data-order', 'data-delimiter', 'data-range', 'data-range-separator', 'data-options'],
    });

    cleanup(() => observer.disconnect());
  });

  Alpine.magic('dateFormat', () => {
    const make = (config) => createDateFormatter({ ...config, locale: resolveLocale(config && config.locale) });
    const fn = (value, config) => {
      const formatter = make(config || {});
      // A `{ start, end }` object formats as a range; anything else is a single date.
      if (value && typeof value === 'object' && !(value instanceof Date) && 'start' in value) {
        const start = valid(toDate(value.start));
        return start ? (formatter.formatRange(start, valid(toDate(value.end))) ?? '') : '';
      }
      const d = valid(toDate(value));
      return d ? formatter.format(d) : '';
    };
    fn.with = (config) => make(config || {});
    return fn;
  });
}
